import { isValid } from 'date-fns';
import type {
  EditPricingBaselineLine,
  SalesModalState,
  SelectedProduct,
} from '../../types/sales.types';
import { roundMoney } from '../money';
import { commercialDateOverrideIso } from './commercial-sale-date';
import { persistNetVsCatalog, type LinePricingFields } from './line-pricing.utils';
import { buildOrderSummary } from './order-summary.utils';

export interface CommercialEditItemPayload {
  id: number;
  markupType: SelectedProduct['markupType'];
  markupValue: number;
  adjustedPrice: number;
  discountType: SelectedProduct['discountType'];
  discountValue: number;
  finalPrice: number;
}

export interface CommercialEditPayload {
  customerId?: number | null;
  hasInvoice: boolean;
  createdById?: string;
  /** Explicit custom commercial datetime (ISO). Mutually exclusive with useCurrentSaleDate. */
  saleDate?: string;
  /** Intent: server stamps saleDate at confirmation time (do not send client clock). */
  useCurrentSaleDate?: boolean;
  confirmZeroSale?: boolean;
  items?: CommercialEditItemPayload[];
  charges?: Array<{ categoryCode: string; amount: number; description?: string }>;
  subtotal: number;
  extraChargesTotal: number;
  totalAmount: number;
  discountScope?: 'PER_ITEM' | 'PER_ORDER';
}

function persistEditLineForSave(
  product: SelectedProduct,
  baseline: EditPricingBaselineLine | undefined,
): LinePricingFields {
  const catalog = roundMoney(baseline?.originalPrice ?? product.originalPrice);
  const acceptedFinal = roundMoney(baseline?.finalPrice ?? product.finalPrice);
  const currentFinal = roundMoney(product.finalPrice);

  if (baseline != null && currentFinal === acceptedFinal) {
    return {
      markupType: baseline.markupType,
      markupValue: roundMoney(baseline.markupValue),
      adjustedPrice: roundMoney(baseline.adjustedPrice),
      discountType: baseline.discountType,
      discountValue: roundMoney(baseline.discountValue),
      finalPrice: acceptedFinal,
    };
  }

  return persistNetVsCatalog(catalog, currentFinal);
}

export function buildCommercialUpdatePayload(state: SalesModalState): CommercialEditPayload {
  if (state.editingOrderId == null) {
    throw new Error('No hay una venta en edición');
  }

  const baselineByProductId = new Map(
    (state.editPricingBaseline ?? []).map((line) => [line.warehouseInventoryProductId, line]),
  );

  const persistedLines = state.selectedProducts.map((product) => {
    const persisted = persistEditLineForSave(
      product,
      baselineByProductId.get(product.warehouseInventoryProduct.id),
    );
    return {
      originalPrice: roundMoney(
        baselineByProductId.get(product.warehouseInventoryProduct.id)?.originalPrice ??
          product.originalPrice,
      ),
      ...persisted,
    };
  });

  const summary = buildOrderSummary({
    lines: persistedLines,
    charges: state.orderCharges,
    payments: state.paymentConfig.payments,
    allowPartialPayment: true,
  });

  const payload: CommercialEditPayload = {
    customerId: state.selectedCustomer?.id ?? null,
    hasInvoice: Boolean(state.hasInvoice),
    createdById: state.selectedSeller?.id,
    subtotal: roundMoney(summary.subtotal),
    extraChargesTotal: roundMoney(summary.totalCharges),
    totalAmount: roundMoney(summary.grandTotal),
    discountScope: state.discountConfig.scope,
  };

  if (state.mode === 'edit') {
    if (state.saleDateMode === 'custom' && isValid(state.saleDate)) {
      payload.saleDate = state.saleDate.toISOString();
    } else if (state.saleDateMode === 'current') {
      // Server stamps confirmation time — never send client wall-clock.
      payload.useCurrentSaleDate = true;
    }
    // `keep` / null → omit date fields (preserve registered commercial date).
  } else {
    const saleDate = commercialDateOverrideIso(state.saleDateMode, state.saleDate);
    if (saleDate != null) {
      payload.saleDate = saleDate;
    }
  }

  if (state.pricingEditable) {
    payload.items = state.selectedProducts.map((product) => {
      if (product.salesOrderItemId == null) {
        throw new Error('Falta el identificador de ítem de la venta');
      }
      return {
        id: product.salesOrderItemId,
        ...persistEditLineForSave(
          product,
          baselineByProductId.get(product.warehouseInventoryProduct.id),
        ),
      };
    });
    payload.charges = state.orderCharges.map((charge) => ({
      categoryCode: charge.categoryCode,
      amount: roundMoney(charge.amount),
      description: charge.description?.trim() || undefined,
    }));
    if (state.selectedProducts.some((product) => roundMoney(product.finalPrice) === 0)) {
      payload.confirmZeroSale = true;
    }
  }

  return payload;
}
